import { t, useLanguage } from './i18n';
import { getWebappApiUrl, webappFetch } from './utils/apiConfig';
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import Login from './pages/Login';

// Inje University용 컴포넌트들
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import NodeManagement from './pages/NodeManagement';
import BlockExplorer from './pages/BlockExplorer';
import SystemMonitor from './pages/SystemMonitor';
import TransactionTracker from './pages/TransactionTracker';
import AccessLogs from './pages/AccessLogs';

// KULS용 컴포넌트들
import KULSMain from './components/KULSMain';

// KAIST용 컴포넌트들
import KAISTMain from './components/KAISTMain';

// EINS용 컴포넌트들
import EINSMain from './components/EINSMain';

// 사용자 타입 정의
interface User {
  username: string;
  displayName: string;
  role: 'admin' | 'kuls' | 'kaist' | 'eins';
  orgName: string;
  orgIcon: string;
  loginTime: string;
}

// External 세션 타임아웃: 30분 비활동 시 자동 로그아웃
const EXTERNAL_SESSION_TIMEOUT = 30 * 60 * 1000;

// LAN 여부 판별 (인제대 LAN = 192.168.x.x, localhost)
const isExternalAccess = (): boolean => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) return false;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return false;
  return true;
};

function App() {
  useLanguage();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // 로그아웃 함수
  const handleLogout = () => {
    try {
      webappFetch(`${getWebappApiUrl()}/api/auth/logout`, { method: 'POST' });
    } catch (e) {}
    localStorage.removeItem('socfai_lastActivity');
    localStorage.setItem('socfai_logout_event', Date.now().toString());
    setUser(null);
  };

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        const response = await webappFetch(`${getWebappApiUrl()}/api/auth/me`);
        const body = await response.json();
        if (response.ok && body.success) {
          setUser({
            ...body.user,
            loginTime: new Date().toISOString()
          });
          localStorage.setItem('socfai_lastActivity', Date.now().toString());
        }
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthState();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'socfai_logout_event') {
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Heartbeat: 로그인 상태일 때 30초마다 활성 상태 전송
  useEffect(() => {
    if (!user) return;
    const sendHeartbeat = () => {
      try {
        webappFetch(`${getWebappApiUrl()}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            screenResolution: `${window.screen.width}x${window.screen.height}`
          })
        });
      } catch (e) {}
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // External 접속: 사용자 활동 추적 + 30분 비활동 자동 로그아웃
  useEffect(() => {
    if (!user || !isExternalAccess()) return;

    // 활동 감지 → lastActivity 갱신 (1분 throttle)
    let lastUpdate = Date.now();
    const trackActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        lastUpdate = now;
        localStorage.setItem('socfai_lastActivity', now.toString());
      }
    };

    const events: string[] = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, trackActivity, { passive: true }));

    // 60초마다 세션 만료 체크
    const checkExpiration = () => {
      const lastActivity = parseInt(localStorage.getItem('socfai_lastActivity') || '0');
      if (lastActivity > 0 && Date.now() - lastActivity > EXTERNAL_SESSION_TIMEOUT) {
        try {
          webappFetch(`${getWebappApiUrl()}/api/auth/logout`, { method: 'POST' });
        } catch (e) {}
        localStorage.removeItem('socfai_lastActivity');
        localStorage.setItem('socfai_logout_event', Date.now().toString());
        setUser(null);
      }
    };
    const intervalId = setInterval(checkExpiration, 60000);

    return () => {
      events.forEach(e => window.removeEventListener(e, trackActivity));
      clearInterval(intervalId);
    };
  }, [user]);

  // 로딩 중 화면
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2, color: '#1976d2' }} />
        <Typography variant="h6" sx={{ color: '#1976d2' }}>{t("시스템 초기화 중...")}</Typography>
      </Box>
    );
  }

  // 로그인하지 않은 경우
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // 로그인한 경우 - 역할에 따른 UI 분기
  // 필요시에만 로그 출력 (디버깅용)
  // console.log('🔐 현재 로그인 사용자:', user!.role, user!.displayName);

  // 역할별 메인 컴포넌트 렌더링
  switch(user!.role) {
    case 'admin':
      return (
        <Box sx={{ display: 'flex' }}>
          <Header user={user} onMenuClick={toggleSidebar} onLogout={handleLogout} />
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <Container 
            component="main" 
            maxWidth="xl"
            sx={{ 
              flexGrow: 1, 
              padding: '24px',
              marginTop: 9,  // Header 높이에 맞춤
              marginLeft: sidebarOpen ? 15 : 8,  // 사이드바 상태별 최적화
              marginRight: 8,  // 우측 간격 추가
              transition: 'margin-left 0.3s ease'
            }}
          >
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/nodes" element={<NodeManagement />} />
              <Route path="/blocks" element={<BlockExplorer />} />
              <Route path="/containers" element={<Navigate to="/blocks" replace />} />
              <Route path="/transactions" element={<TransactionTracker />} />
              <Route path="/monitor" element={<SystemMonitor />} />
              <Route path="/access-logs" element={<AccessLogs />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Container>
        </Box>
      );

    case 'kuls':
      return <KULSMain user={user} onLogout={handleLogout} />;

    case 'kaist':
      return <KAISTMain user={user} onLogout={handleLogout} />;

    case 'eins':
      return <EINSMain user={user} onLogout={handleLogout} />;

    default:
      // 알 수 없는 역할인 경우 로그아웃 처리
      handleLogout();
      return null;
  }
}

export default App;
